import React, { useCallback, useEffect, useRef } from "react";
import Backbone from "backbone";
import appStatusManagement from "app/components/flow/designer/appStatusManagement";
import { useAppDispatch, useAppSelector } from "src/stores/redux/hooks";
import { getAppModel, getIsAutomatedGroup } from "../helpers/selectors";
import {
    closeAllSidebarsAction,
    setShowAppErrorsSidebarAction,
    setShowValidationErrorsSidebarAction,
    setLoadLocalErrorsAction
} from "src/stores/redux/features/flowdesigner/flowdesigner.slice";
import { useAutomatedGroup } from "../helpers/app.utils";
import navigateTo from "src/navigate-to";
import AppListService from "../../../apps/pages/app-list/app-list-service";

interface AppStatusManagementWrapperProps {
    appName?: string;
    flowName?: string;
    promptIfUnsavedChanged?: () => Promise<boolean>;
}

const AppStatusManagementWrapper: React.FC<AppStatusManagementWrapperProps> = ({
    appName,
    flowName,
    promptIfUnsavedChanged
}) => {
    const containerRef = useRef<HTMLUListElement>(null);
    const viewRef = useRef<any>(null);

    const dispatch = useAppDispatch();
    const app = useAppSelector(getAppModel);
    const isAutomatedGroup = useAppSelector(getIsAutomatedGroup);
    const group = useAutomatedGroup();

    const handleShowAppErrors = useCallback(
        (loadLocal: boolean) => {
            dispatch(closeAllSidebarsAction({ appName, flowName }));
            dispatch(setShowAppErrorsSidebarAction(true));
            dispatch(setLoadLocalErrorsAction(loadLocal));
            navigateTo.AppErrors(appName);
        },
        [dispatch, appName, flowName]
    );

    const handleShowValidationErrors = useCallback(() => {
        dispatch(closeAllSidebarsAction({ appName, flowName }));
        dispatch(setShowValidationErrorsSidebarAction(true));
    }, [dispatch, appName, flowName]);

    const handleGoToAutomationPipeline = useCallback(() => {
        if (group) {
            AppListService.setSelectedGroup(group);
            navigateTo.Apps();
        } else {
            console.warn("No group found for current app, cannot navigate to automation pipeline");
        }
    }, [group]);

    useEffect(() => {
        if (!containerRef.current || !app) {
            return;
        }

        // Extract the app object - it might be a Backbone model or a plain object
        let appObject: any;
        if (app.toJSON && typeof app.toJSON === "function") {
            appObject = app.toJSON();
        } else if (app.get && typeof app.get === "function") {
            // It's a Backbone model, extract properties manually
            appObject = {
                id: app.get("id"),
                name: app.get("name"),
                nsName: app.get("nsName"),
                flowStatus: app.get("flowStatus"),
                type: app.get("type"),
                metaInfoStatus: app.get("metaInfoStatus"),
                isEditable: app.get("isEditable")
            };
        } else {
            // It's already a plain object
            appObject = app;
        }

        // Ensure the app object has the required properties
        if (!appObject || !appObject.id) {
            console.error("App object is missing required 'id' property", { app, appObject });
            return;
        }

        // Create the Backbone model and set app as a direct property (not an attribute)
        // This matches how the original designer sets graphModel.app
        const model: any = new Backbone.Model();
        model.app = appObject;

        // Create the Marionette view
        const appStatusView = new appStatusManagement({
            model: model,
            isAutomatedGroup: isAutomatedGroup,
            moveToAppGroups: handleGoToAutomationPipeline
        });

        // Set the onBeforeDeploy callback to use promptIfUnsavedChanged
        // Convert the boolean-returning promptIfUnsavedChanged to the Promise<void> format expected by onBeforeDeploy
        if (promptIfUnsavedChanged) {
            appStatusView.onBeforeDeploy = async () => {
                const canProceed = await promptIfUnsavedChanged();
                if (!canProceed) {
                    return Promise.reject();
                }
                return Promise.resolve();
            };
        } else {
            // Provide a default implementation that resolves immediately
            appStatusView.onBeforeDeploy = () => Promise.resolve();
        }

        // Set the promptIfUnsavedChanged callback for status change interception
        if (promptIfUnsavedChanged) {
            appStatusView.promptIfUnsavedChanged = promptIfUnsavedChanged;
        }

        // Listen to events from the view
        appStatusView.on("show-app-errors", handleShowAppErrors);
        appStatusView.on("show-validation-errors", handleShowValidationErrors);

        // Render the view into the container
        containerRef.current.appendChild(appStatusView.render().el);
        appStatusView.triggerMethod("show");

        // Store the view reference for cleanup
        viewRef.current = appStatusView;

        // Cleanup function
        return () => {
            if (viewRef.current) {
                viewRef.current.destroy();
                viewRef.current = null;
            }
        };
    }, [
        app,
        isAutomatedGroup,
        handleGoToAutomationPipeline,
        handleShowAppErrors,
        handleShowValidationErrors,
        promptIfUnsavedChanged
    ]);

    return (
        <ul
            ref={containerRef}
            className="app-status"
            style={{
                display: "flex",
                alignItems: "center",
                listStyle: "none",
                margin: 0,
                padding: 0
            }}
        />
    );
};

export default AppStatusManagementWrapper;
